import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';

interface OfferFormState {
  id?: number;
  productId: number | '';
  discountPercent: number;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
}

const createEmptyOfferFormState = (defaultProductId?: number): OfferFormState => ({
  id: undefined,
  productId: defaultProductId ?? '',
  discountPercent: 0,
  startsAt: '',
  endsAt: '',
  isActive: true,
});

const formatDateDisplay = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
};

const SpecialOffersManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('SpecialOffersManagement deve ser usado dentro de AppProvider');
  }

  const {
    adminProducts,
    specialOffers,
    addSpecialOffer,
    updateSpecialOffer,
    deleteSpecialOffer,
  } = context;

  const productOptions = useMemo(
    () => adminProducts.map(product => ({ value: product.id, label: product.name })),
    [adminProducts],
  );

  const [formState, setFormState] = useState<OfferFormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasProducts = productOptions.length > 0;
  const isEditing = Boolean(formState?.id);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormState(prev => {
      if (!prev) return prev;
      if (name === 'productId') {
        return { ...prev, productId: value ? Number(value) : '' };
      }
      if (name === 'discountPercent') {
        return { ...prev, discountPercent: Number(value) };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormState(prev => (prev ? { ...prev, isActive: checked } : prev));
  };

  const startCreating = () => {
    setFormState(createEmptyOfferFormState(productOptions[0]?.value));
    setFeedback(null);
    setError(null);
  };

  const startEditing = (offerId: number) => {
    const offer = specialOffers.find(item => item.id === offerId);
    if (!offer) return;

    setFormState({
      id: offer.id,
      productId: offer.productId,
      discountPercent: offer.discountPercentage,
      startsAt: offer.startDate ? offer.startDate.slice(0, 10) : '',
      endsAt: offer.endDate ? offer.endDate.slice(0, 10) : '',
      isActive: offer.isActive,
    });
    setFeedback(null);
    setError(null);
  };

  const cancelEditing = () => {
    setFormState(null);
    setFeedback(null);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState) return;

    if (!formState.productId) {
      setError('Selecione um produto para criar a oferta.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setFeedback(null);

    try {
      if (formState.id) {
        await updateSpecialOffer(formState.id, {
          productId: formState.productId,
          discountPercent: formState.discountPercent,
          startsAt: formState.startsAt || null,
          endsAt: formState.endsAt || null,
          isActive: formState.isActive,
        });
        setFeedback('Oferta atualizada com sucesso.');
      } else {
        await addSpecialOffer({
          productId: formState.productId,
          discountPercent: formState.discountPercent,
          startsAt: formState.startsAt || null,
          endsAt: formState.endsAt || null,
        });
        setFeedback('Oferta criada com sucesso.');
      }
      setFormState(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar oferta.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (offerId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja excluir esta oferta?');
    if (!confirmation) return;

    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await deleteSpecialOffer(offerId);
      setFeedback('Oferta removida com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover oferta.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (offerId: number, isActive: boolean) => {
    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await updateSpecialOffer(offerId, { isActive: !isActive });
      setFeedback(`Oferta ${isActive ? 'desativada' : 'ativada'} com sucesso.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status da oferta.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Ofertas</h2>
        <div className="section-actions">
          <button
            type="button"
            className="add-button"
            onClick={startCreating}
            disabled={submitting || !hasProducts}
          >
            Nova oferta
          </button>
        </div>
      </div>

      {!hasProducts && (
        <p className="warning">Cadastre ao menos um produto antes de criar ofertas.</p>
      )}

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {formState && (
        <form onSubmit={handleSubmit} className="admin-form admin-offer-form">
          <h3>{isEditing ? 'Editar oferta' : 'Nova oferta'}</h3>
          <label>
            Produto
            <select
              name="productId"
              value={formState.productId}
              onChange={handleChange}
              disabled={submitting || !hasProducts}
              required
            >
              <option value="">Selecione um produto</option>
              {productOptions.map(option => (
                <option key={`product-option-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Desconto (%)
            <input
              type="number"
              name="discountPercent"
              min={0}
              max={100}
              step={0.5}
              value={formState.discountPercent}
              onChange={handleChange}
              disabled={submitting}
              required
            />
          </label>
          <label>
            Início (opcional)
            <input
              type="date"
              name="startsAt"
              value={formState.startsAt}
              onChange={handleChange}
              disabled={submitting}
            />
          </label>
          <label>
            Fim (opcional)
            <input
              type="date"
              name="endsAt"
              value={formState.endsAt}
              onChange={handleChange}
              disabled={submitting}
            />
          </label>
          {formState.id && (
            <label>
              Ativa
              <input
                type="checkbox"
                checked={formState.isActive}
                onChange={handleCheckboxChange}
                disabled={submitting}
              />
            </label>
          )}
          <div className="form-actions">
            <button type="submit" className="save-button" disabled={submitting}>
              {isEditing ? 'Atualizar' : 'Criar'}
            </button>
            <button type="button" className="cancel-button" onClick={cancelEditing} disabled={submitting}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {specialOffers.length === 0 ? (
        <p>Nenhuma oferta cadastrada até o momento.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Produto</th>
              <th>Desconto</th>
              <th>Início</th>
              <th>Fim</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {specialOffers.map(offer => {
              const product = adminProducts.find(item => item.id === offer.productId);
              return (
                <tr key={`special-offer-${offer.id}`}>
                  <td>{offer.id}</td>
                  <td>{product ? product.name : `Produto #${offer.productId}`}</td>
                  <td>{offer.discountPercentage}%</td>
                  <td>{formatDateDisplay(offer.startDate)}</td>
                  <td>{formatDateDisplay(offer.endDate)}</td>
                  <td>{offer.isActive ? 'Ativa' : 'Inativa'}</td>
                  <td className="actions">
                    <div className="admin-actions">
                      <button
                        type="button"
                        className="icon-button icon-button--edit"
                        onClick={() => startEditing(offer.id)}
                        disabled={submitting}
                        title="Editar oferta"
                      >
                        ✏️
                        <span className="sr-only">Editar</span>
                      </button>
                      <button
                        type="button"
                        className={`icon-button icon-button--toggle ${offer.isActive ? 'is-active' : 'is-inactive'}`}
                        onClick={() => handleToggleActive(offer.id, offer.isActive)}
                        disabled={submitting}
                        title={offer.isActive ? 'Desativar oferta' : 'Ativar oferta'}
                      >
                        {offer.isActive ? '⏸' : '▶'}
                        <span className="sr-only">
                          {offer.isActive ? 'Desativar' : 'Ativar'}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="icon-button icon-button--delete"
                        onClick={() => handleDelete(offer.id)}
                        disabled={submitting}
                        title="Excluir oferta"
                      >
                        🗑️
                        <span className="sr-only">Excluir</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SpecialOffersManagement;